'use client';

import { useState, useCallback, useRef, useEffect } from 'react';

import styles from './uploadMethodMenu.module.scss';

type UploadMethodMenuBaseProps = {
  onUploadNew: () => void;
  onChooseExisting: () => void;
  disabled?: boolean;
  direction?: 'up' | 'down';
};

type RenderPropMode = UploadMethodMenuBaseProps & {
  children: (props: {
    onClick: () => void;
    isOpen: boolean;
  }) => React.ReactNode;
  isOpen?: never;
  onClose?: never;
};

type ControlledMode = UploadMethodMenuBaseProps & {
  isOpen: boolean;
  onClose: () => void;
  children?: never;
};

type UploadMethodMenuProps = RenderPropMode | ControlledMode;

function MenuItems({
  onUploadNew,
  onChooseExisting,
  direction = 'down'
}: {
  onUploadNew: () => void;
  onChooseExisting: () => void;
  direction?: 'up' | 'down';
}) {
  return (
    <div
      className={`${styles.menu} ${direction === 'up' ? styles.menuUp : ''}`}
      role="menu"
    >
      <button
        type="button"
        className={styles.menuItem}
        onClick={onUploadNew}
        role="menuitem"
      >
        <span className={styles.menuIcon}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
        </span>
        Upload new
      </button>
      <button
        type="button"
        className={styles.menuItem}
        onClick={onChooseExisting}
        role="menuitem"
      >
        <span className={styles.menuIcon}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <polyline points="21 15 16 10 5 21" />
          </svg>
        </span>
        Choose from library
      </button>
    </div>
  );
}

export function UploadMethodMenu(props: UploadMethodMenuProps) {
  const { onUploadNew, onChooseExisting, disabled, direction } = props;

  const isControlled = 'isOpen' in props && props.isOpen !== undefined;

  const [internalOpen, setInternalOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const isOpen = isControlled ? props.isOpen : internalOpen;

  const close = useCallback(() => {
    if (isControlled) {
      props.onClose();
    } else {
      setInternalOpen(false);
    }
  }, [isControlled, props]);

  const toggle = useCallback(() => {
    if (disabled) return;
    if (isControlled) return;
    setInternalOpen((prev) => !prev);
  }, [disabled, isControlled]);

  const handleUploadNew = useCallback(() => {
    close();
    onUploadNew();
  }, [close, onUploadNew]);

  const handleChooseExisting = useCallback(() => {
    close();
    onChooseExisting();
  }, [close, onChooseExisting]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        close();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, close]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, close]);

  if (isControlled) {
    if (!isOpen) return null;
    return (
      <div className={styles.anchor} ref={menuRef}>
        <MenuItems
          onUploadNew={handleUploadNew}
          onChooseExisting={handleChooseExisting}
          direction={direction}
        />
      </div>
    );
  }

  return (
    <div className={styles.anchor} ref={menuRef}>
      {props.children({ onClick: toggle, isOpen })}
      {isOpen && (
        <MenuItems
          onUploadNew={handleUploadNew}
          onChooseExisting={handleChooseExisting}
          direction={direction}
        />
      )}
    </div>
  );
}
